// The desktop bridge reference (window.moodsDesktop, exposed by apps/desktop's preload) without any app imports, so
// low-level modules (store/auth, lib/api) can use it without import cycles. Everything else goes through
// lib/desktop.ts. In a browser `desktop` is null and every helper here is a no-op. The token helpers also cover the
// phone apps, whose web builds run on app origins too (the Keychain / the Android Keystore through lib/native-bridge.ts).
import type { MoodsDesktopBridge } from '@moods/shared';
import { getNativeSecret, nativeApp, setNativeSecret } from './native-bridge';

export const desktop: MoodsDesktopBridge | null =
  typeof window !== 'undefined' ? (window.moodsDesktop ?? null) : null;

/** Refresh-token persistence for lib/api's refresh flow (OS-encrypted storage in the desktop shell, the phone apps' secure storage). */
export const shellTokenHooks: {
  getStoredRefreshToken?: () => Promise<string | null>;
  storeRefreshToken?: (token: string | null) => Promise<void>;
} = desktop
  ? {
      getStoredRefreshToken: () => desktop.auth.getRefreshToken().catch(() => null),
      storeRefreshToken: token => desktop.auth.setRefreshToken(token).catch(() => undefined),
    }
  : nativeApp
    ? {
        getStoredRefreshToken: () => getNativeSecret('refreshToken'),
        storeRefreshToken: token => setNativeSecret('refreshToken', token),
      }
    : {};

/** Remembers / forgets the app's refresh token (no-op on the web). */
export function storeShellRefreshToken(token: string | null | undefined): void {
  if (token === undefined) return;
  if (desktop) void desktop.auth.setRefreshToken(token).catch(() => undefined);
  else if (nativeApp) void setNativeSecret('refreshToken', token);
}

/**
 * The app's "known device" token (new-device verification): the API returns it in the body for the desktop, iOS and
 * Android platforms, the shell keeps it (encrypted / in secure storage) across sign-outs, logins send it back. Browsers
 * use the moods_dt cookie.
 */
export async function shellDeviceToken(): Promise<string | undefined> {
  if (nativeApp) return (await getNativeSecret('deviceToken')) ?? undefined;
  if (!desktop?.auth.getDeviceToken) return undefined;
  return (await desktop.auth.getDeviceToken().catch(() => null)) ?? undefined;
}

export function storeShellDeviceToken(token: string | undefined): void {
  if (!token) return;
  if (nativeApp) void setNativeSecret('deviceToken', token);
  else if (desktop?.auth.setDeviceToken) void desktop.auth.setDeviceToken(token).catch(() => undefined);
}
